import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { equityCurve } from '../../lib/equity-curve';
import { Sparkline } from '../charts/Sparkline';
import { Chip } from '../primitives/Chip';
import { Tag } from '../primitives/Tag';
import type { Strategy } from '../../content/schemas';

interface StrategyCardProps {
  strategy: Strategy;
}

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: ${(p) => p.theme.step[5]}px;
  border: 1px solid ${(p) => p.theme.color.border.soft};
  border-radius: 10px;
  background: ${(p) => p.theme.color.bg[1]};
  color: inherit;
  transition: border-color 160ms ${(p) => p.theme.ease}, transform 160ms ${(p) => p.theme.ease};

  &:hover {
    border-color: ${(p) => p.theme.color.border.strong};
    transform: translateY(-2px);
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Code = styled.span`
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[0]}px;
  color: ${(p) => p.theme.color.text[3]};
`;

const Name = styled.h3`
  margin: 0;
  font-family: ${(p) => p.theme.font.serif};
  font-size: ${(p) => p.theme.step[6]}px;
  letter-spacing: -0.01em;
`;

const Tagline = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[3]}px;
  color: ${(p) => p.theme.color.text[2]};
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Label = styled.span`
  font-size: ${(p) => p.theme.step[0]}px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.color.text[3]};
`;

const Value = styled.span`
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[4]}px;
  color: ${(p) => p.theme.color.text[0]};
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Tags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SparkWrap = styled.div<{ $color: string }>`
  color: ${(p) => p.$color};
`;

function colorFor(theme: { color: { category: Record<string, string> } }, key: string) {
  return theme.color.category[key as keyof typeof theme.color.category] ?? theme.color.category.amber;
}

export function StrategyCard({ strategy }: StrategyCardProps) {
  const data = equityCurve({ seed: strategy.id.length * 17 + strategy.trades, steps: 64 });
  return (
    <Card to={`/strategies/${strategy.id}`}>
      <Top>
        <Code>{strategy.code}</Code>
        <Chip $variant={strategy.status === 'live' ? 'up' : 'flat'}>{strategy.status}</Chip>
      </Top>
      <div>
        <Name>{strategy.name}</Name>
        <Tagline>{strategy.tagline}</Tagline>
      </div>
      <SparkWrap
        $color={
          // small inline lookup (no theme hook needed since the chart accepts currentColor)
          ({
            amber: '#ff9d2e',
            cyan: '#5ec9f5',
            violet: '#a88dfb',
            rose: '#f599b8',
            lime: '#c4e85c',
          }[strategy.color] ?? '#ff9d2e')
        }
      >
        <Sparkline data={data} width={280} height={40} />
      </SparkWrap>
      <Metrics>
        <Metric>
          <Label>Sharpe</Label>
          <Value>{strategy.sharpe.toFixed(2)}</Value>
        </Metric>
        <Metric>
          <Label>CAGR</Label>
          <Value>{strategy.cagr}%</Value>
        </Metric>
        <Metric>
          <Label>MaxDD</Label>
          <Value>{strategy.maxDD}%</Value>
        </Metric>
        <Metric>
          <Label>Win</Label>
          <Value>{strategy.winRate}%</Value>
        </Metric>
      </Metrics>
      <Bottom>
        <Tags>
          <Tag>{strategy.category}</Tag>
          <Tag>{strategy.style}</Tag>
        </Tags>
        <span style={{ fontFamily: 'inherit', fontSize: 12 }}>
          <span style={{ opacity: 0.6 }}>Capacity </span>
          <span>{strategy.capacity}%</span>
        </span>
      </Bottom>
    </Card>
  );
}

export { colorFor };
